/**
 * Modal compartido para crear / editar un rol con checkboxes de permisos
 * agrupados por módulo. Sirve tanto para roles canónicos como para custom
 * de tenant — el caller decide a qué endpoint mandar.
 */
import { useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useMutation, useQuery } from "@tanstack/react-query";
import { CheckSquare, Save, ShieldCheck, Square } from "lucide-react";

import { useToast } from "../../components/Toast";
import { Modal } from "../../components/Modal";
import {
  Badge,
  Button,
  Cargando,
  useDescarteProtegido,
  useHayCambios,
} from "../../components/ui";
import { ApiError } from "../../lib/api";
import {
  actualizarRol,
  crearRolCanonico,
  crearRolEmpresa,
  obtenerCatalogoPermisos,
  type CrearRolPayload,
  type RolRead,
} from "./api";

const codeRe = /^[a-z0-9_-]+$/;

const baseSchema = z.object({
  nombre: z.string().min(1, "Requerido").max(120),
  descripcion: z.string().max(300).optional().or(z.literal("")),
});

const createSchema = baseSchema.extend({
  code: z
    .string()
    .min(1, "Requerido")
    .max(64)
    .regex(codeRe, "Solo minúsculas, números, guión y guión bajo"),
});

type CreateValues = z.infer<typeof createSchema>;
type EditValues = z.infer<typeof baseSchema>;

type Mode =
  | { kind: "create-canonico" }
  | { kind: "create-empresa"; empresaId: number }
  | { kind: "edit"; rol: RolRead };

interface Props {
  mode: Mode;
  onClose: () => void;
  onSuccess: () => void;
}

const CREATE_FORM_ID = "rol-create-form";
const EDIT_FORM_ID = "rol-edit-form";

export function RolFormModal(props: Props): JSX.Element {
  const initialPermisos = props.mode.kind === "edit" ? props.mode.rol.permisos : [];
  const [permisos, setPermisos] = useState<string[]>(initialPermisos);

  const catalogo = useQuery({
    queryKey: ["superadmin", "permisos"],
    queryFn: obtenerCatalogoPermisos,
  });

  const tieneWildcard = permisos.includes("*");

  const titulo =
    props.mode.kind === "edit"
      ? `Editar rol · ${props.mode.rol.nombre}`
      : props.mode.kind === "create-empresa"
        ? "Nuevo rol del comité"
        : "Nuevo rol canónico";

  if (props.mode.kind === "edit") {
    return (
      <EditForm
        titulo={titulo}
        rol={props.mode.rol}
        permisos={permisos}
        tieneWildcard={tieneWildcard}
        catalogo={catalogo.data?.grupos ?? []}
        loadingCatalogo={catalogo.isLoading}
        onTogglePermiso={(p) => togglePermiso(setPermisos, p)}
        onClose={props.onClose}
        onSuccess={props.onSuccess}
      />
    );
  }

  return (
    <CreateForm
      titulo={titulo}
      mode={props.mode}
      permisos={permisos}
      tieneWildcard={tieneWildcard}
      catalogo={catalogo.data?.grupos ?? []}
      loadingCatalogo={catalogo.isLoading}
      onTogglePermiso={(p) => togglePermiso(setPermisos, p)}
      onClose={props.onClose}
      onSuccess={props.onSuccess}
    />
  );
}

function togglePermiso(
  setter: React.Dispatch<React.SetStateAction<string[]>>,
  permiso: string,
): void {
  setter((prev) =>
    prev.includes(permiso) ? prev.filter((p) => p !== permiso) : [...prev, permiso],
  );
}

interface PermisoGrupoUI {
  code: string;
  titulo: string;
  permisos: { code: string; etiqueta: string }[];
}

function CreateForm({
  titulo,
  mode,
  permisos,
  tieneWildcard,
  catalogo,
  loadingCatalogo,
  onTogglePermiso,
  onClose,
  onSuccess,
}: {
  titulo: string;
  mode: { kind: "create-canonico" } | { kind: "create-empresa"; empresaId: number };
  permisos: string[];
  tieneWildcard: boolean;
  catalogo: PermisoGrupoUI[];
  loadingCatalogo: boolean;
  onTogglePermiso: (p: string) => void;
  onClose: () => void;
  onSuccess: () => void;
}): JSX.Element {
  const toast = useToast();
  const form = useForm<CreateValues>({
    resolver: zodResolver(createSchema),
    defaultValues: { code: "", nombre: "", descripcion: "" },
  });

  const mut = useMutation({
    mutationFn: (values: CreateValues) => {
      const payload: CrearRolPayload = {
        code: values.code,
        nombre: values.nombre,
        descripcion: values.descripcion || null,
        permisos,
      };
      if (mode.kind === "create-empresa") {
        return crearRolEmpresa(mode.empresaId, payload);
      }
      return crearRolCanonico(payload);
    },
    onSuccess: (rol) => {
      toast.success(`Rol ${rol.nombre} creado`);
      onSuccess();
    },
    onError: (err) =>
      toast.error(err instanceof ApiError ? err.message : "No se pudo crear el rol"),
  });

  // Cambios sin guardar: cerrar pregunta antes de descartar (auditoría UX-01).
  // watch() da todos los campos del formulario (react-hook-form).
  const hayCambios = useHayCambios([form.watch()]);
  const { cerrar: intentarCerrar, dialogo: dialogoDescarte } = useDescarteProtegido(
    hayCambios,
    onClose,
    mut.isPending,
  );

  return (
    <Modal
      open
      onClose={intentarCerrar}
      title={titulo}
      icon={ShieldCheck}
      size="xl"
      footer={
        <Footer formId={CREATE_FORM_ID} onClose={intentarCerrar} pending={mut.isPending}>
          {mut.isPending ? "Creando…" : "Crear rol"}
        </Footer>
      }
    >
      <form
        id={CREATE_FORM_ID}
        onSubmit={form.handleSubmit((v) => mut.mutate(v))}
        className="space-y-4"
      >
        <div className="grid grid-cols-2 gap-3">
          <Field label="Code (técnico)" error={form.formState.errors.code?.message}>
            <input
              {...form.register("code")}
              className={`${CONTROL} font-mono`}
              placeholder="recaudador"
            />
          </Field>
          <Field label="Nombre" error={form.formState.errors.nombre?.message}>
            <input
              {...form.register("nombre")}
              className={CONTROL}
              placeholder="Recaudador"
            />
          </Field>
        </div>
        <Field label="Descripción" error={form.formState.errors.descripcion?.message}>
          <input
            {...form.register("descripcion")}
            className={CONTROL}
            placeholder="Quién y para qué se usa este rol"
          />
        </Field>

        <PermisosPicker
          catalogo={catalogo}
          loading={loadingCatalogo}
          permisos={permisos}
          tieneWildcard={tieneWildcard}
          onToggle={onTogglePermiso}
        />
      </form>
      {dialogoDescarte}
    </Modal>
  );
}

function EditForm({
  titulo,
  rol,
  permisos,
  tieneWildcard,
  catalogo,
  loadingCatalogo,
  onTogglePermiso,
  onClose,
  onSuccess,
}: {
  titulo: string;
  rol: RolRead;
  permisos: string[];
  tieneWildcard: boolean;
  catalogo: PermisoGrupoUI[];
  loadingCatalogo: boolean;
  onTogglePermiso: (p: string) => void;
  onClose: () => void;
  onSuccess: () => void;
}): JSX.Element {
  const toast = useToast();
  const form = useForm<EditValues>({
    resolver: zodResolver(baseSchema),
    defaultValues: {
      nombre: rol.nombre,
      descripcion: rol.descripcion ?? "",
    },
  });

  const mut = useMutation({
    mutationFn: (values: EditValues) =>
      actualizarRol(rol.id, {
        nombre: values.nombre,
        descripcion: values.descripcion || null,
        permisos,
      }),
    onSuccess: (r) => {
      toast.success(`Rol ${r.nombre} actualizado`);
      onSuccess();
    },
    onError: (err) =>
      toast.error(err instanceof ApiError ? err.message : "No se pudo guardar"),
  });

  // Cambios sin guardar: cerrar pregunta antes de descartar (auditoría UX-01).
  // watch() da todos los campos del formulario (react-hook-form).
  const hayCambios = useHayCambios([form.watch()]);
  const { cerrar: intentarCerrar, dialogo: dialogoDescarte } = useDescarteProtegido(
    hayCambios,
    onClose,
    mut.isPending,
  );

  return (
    <Modal
      open
      onClose={intentarCerrar}
      title={titulo}
      icon={ShieldCheck}
      size="xl"
      footer={
        <Footer formId={EDIT_FORM_ID} onClose={intentarCerrar} pending={mut.isPending}>
          {mut.isPending ? "Guardando…" : "Guardar cambios"}
        </Footer>
      }
    >
      <form
        id={EDIT_FORM_ID}
        onSubmit={form.handleSubmit((v) => mut.mutate(v))}
        className="space-y-4"
      >
        <div className="grid grid-cols-2 gap-3">
          <Field label="Code" error={undefined}>
            <input
              value={rol.code}
              disabled
              className={`${CONTROL} bg-neutral-100 font-mono text-neutral-500`}
            />
          </Field>
          <Field label="Nombre" error={form.formState.errors.nombre?.message}>
            <input {...form.register("nombre")} className={CONTROL} />
          </Field>
        </div>
        <Field label="Descripción" error={form.formState.errors.descripcion?.message}>
          <input {...form.register("descripcion")} className={CONTROL} />
        </Field>

        <PermisosPicker
          catalogo={catalogo}
          loading={loadingCatalogo}
          permisos={permisos}
          tieneWildcard={tieneWildcard}
          onToggle={onTogglePermiso}
        />
      </form>
      {dialogoDescarte}
    </Modal>
  );
}

function PermisosPicker({
  catalogo,
  loading,
  permisos,
  tieneWildcard,
  onToggle,
}: {
  catalogo: PermisoGrupoUI[];
  loading: boolean;
  permisos: string[];
  tieneWildcard: boolean;
  onToggle: (p: string) => void;
}): JSX.Element {
  const total = useMemo(
    () => catalogo.reduce((acc, g) => acc + g.permisos.length, 0),
    [catalogo],
  );
  const seleccionados = permisos.filter((p) => p !== "*").length;

  return (
    <div className="space-y-3 border-t border-neutral-100 pt-4">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold text-neutral-900">Permisos</h3>
        <span className="text-xs text-neutral-500">
          {tieneWildcard ? (
            <Badge tone="amber">
              Wildcard activado: tiene TODOS los permisos
            </Badge>
          ) : (
            <>
              {seleccionados} / {total} seleccionados
            </>
          )}
        </span>
      </div>

      {loading && <Cargando inline texto="Cargando catálogo…" />}

      {!loading &&
        catalogo.map((grupo) => (
          <fieldset
            key={grupo.code}
            className="rounded-lg border border-neutral-200 bg-neutral-50 p-3"
          >
            <legend className="px-1 text-xs font-semibold uppercase tracking-wider text-neutral-600">
              {grupo.titulo}
            </legend>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5 mt-1">
              {grupo.permisos.map((p) => {
                const checked = tieneWildcard || permisos.includes(p.code);
                return (
                  <label
                    key={p.code}
                    className={
                      tieneWildcard
                        ? "flex items-start gap-2 px-2 py-1.5 text-sm text-neutral-500"
                        : "flex items-start gap-2 px-2 py-1.5 text-sm text-neutral-700 hover:bg-white rounded cursor-pointer"
                    }
                  >
                    <input
                      type="checkbox"
                      checked={checked}
                      disabled={tieneWildcard}
                      onChange={() => onToggle(p.code)}
                      className="mt-0.5"
                    />
                    <span>
                      <span className="block">{p.etiqueta}</span>
                      <span className="block text-xs font-mono text-neutral-500">
                        {p.code}
                      </span>
                    </span>
                  </label>
                );
              })}
            </div>
          </fieldset>
        ))}

      <div>
        <label className="inline-flex items-center gap-2 text-sm text-amber-900">
          <input
            type="checkbox"
            checked={tieneWildcard}
            onChange={() => onToggle("*")}
          />
          {tieneWildcard ? (
            <CheckSquare className="h-4 w-4 text-amber-700" strokeWidth={1.5} />
          ) : (
            <Square className="h-4 w-4 text-neutral-500" strokeWidth={1.5} />
          )}
          <span>
            <strong>Wildcard ★</strong> — todos los permisos del sistema (recomendado
            solo para el rol "admin")
          </span>
        </label>
      </div>
    </div>
  );
}

const CONTROL =
  "block w-full rounded-md border border-neutral-300 bg-white px-3 py-2 text-sm " +
  "shadow-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500";

function Footer({
  formId,
  onClose,
  pending,
  children,
}: {
  formId: string;
  onClose: () => void;
  pending: boolean;
  children: React.ReactNode;
}): JSX.Element {
  return (
    <>
      <Button variant="secondary" onClick={onClose} disabled={pending}>
        Cancelar
      </Button>
      <Button type="submit" form={formId} icon={Save} disabled={pending}>
        {children}
      </Button>
    </>
  );
}

function Field({
  label,
  error,
  children,
}: {
  label: string;
  error?: string | undefined;
  children: React.ReactNode;
}): JSX.Element {
  return (
    <label className="block text-sm">
      <span className="block font-medium text-neutral-700 mb-1">{label}</span>
      {children}
      {error && <span className="block text-xs text-rose-700 mt-1">{error}</span>}
    </label>
  );
}
